import { useIntl } from 'react-intl';
import { Button, Icon } from 'ui';
import { useNavigate } from 'react-router-dom';
import {
  CartActionType,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import { CartProductCard } from 'ui/Card/CartProductCard';
import { StyledCartDrawer, StyledCartDrawerFooter } from './style';
import { message } from 'antd';
import PublicRoute from 'enums/publicRoutes';
import useMarketType from '../../../utils/useMarketType';
import CartSelect from 'public/components/Selects/CartSelect';
import useQueryApiClient from 'utils/useQueryApiClient';

interface CartDrawerProps {
  isCartDrawerOpen: boolean;
  onCartClose: Function;
}

const CartDrawer = ({ isCartDrawerOpen, onCartClose }: CartDrawerProps) => {
  const intl = useIntl();
  const { farmProducts, priceTotal } = useCartState();
  const navigate = useNavigate();
  const { isEmarket } = useMarketType();
  const dispatchCart = useCartDispatch();

  const { appendData: loadSavedCart } = useQueryApiClient({
    request: {
      url: 'api/v2/cart/:id',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      dispatchCart({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    },
  });

  const showSavedCarts = !isEmarket && farmProducts.length === 0;

  const innerOnClose = () => {
    onCartClose();
  };

  const onGoToCart = () => {
    navigate(isEmarket ? PublicRoute.CART_PAGE : PublicRoute.WHOLESALE_CART);
    onCartClose();
  };

  return (
    <StyledCartDrawer
      id={'cart-drawer'}
      width={502}
      closeIcon={
        <Icon name={'close-basket-drawer-icon'} type={'cartDrawerClose'} />
      }
      open={isCartDrawerOpen}
      onClose={() => innerOnClose()}
      title={intl.formatMessage({ id: 'general.basket' })}
      footer={
        <StyledCartDrawerFooter className={!isEmarket ? 'wholesale' : ''}>
          <div className={'cart-drawer-sum-wrapper'}>
            <div className={'cart-drawer-sum-title'}>
              <p>{intl.formatMessage({ id: 'cart.sum' })}</p>
            </div>
            <div className={'cart-drawer-sum'}>
              {priceTotal.toFixed(2) + ' €'}
            </div>
          </div>
          <Button
            label={intl.formatMessage({
              id: isEmarket
                ? 'general.choose_delivery'
                : 'general.arrange_delivery',
            })}
            type="primary"
            htmlType="submit"
            onClick={() => {
              if (farmProducts.length === 0) {
                message.error(
                  intl.formatMessage({ id: 'cart_summary.empty_warning' })
                );

                return;
              }
              onGoToCart();
            }}
          />
        </StyledCartDrawerFooter>
      }
    >
      {showSavedCarts ? (
        <div className="cart-drawer-saved-carts">
          <CartSelect
            name="cart_id"
            label={intl.formatMessage({ id: 'cart.my_carts' })}
            onChange={(value) => {
              loadSavedCart([], { id: value });
            }}
            layout="vertical"
          />
        </div>
      ) : (
        farmProducts.map((farm, farmIndex) => (
          <div key={farmIndex} className="farm-section">
            {farm.products.map((product, productIndex) => (
              <CartProductCard
                amountCart={product.amount}
                key={productIndex}
                product={product}
              />
            ))}
          </div>
        ))
      )}
    </StyledCartDrawer>
  );
};

export default CartDrawer;
